/**
 * Team distribution algorithms for the Team Builder feature
 */

/**
 * Sort players by average score (descending)
 * @param {Array} players - Array of player objects with average_score
 * @returns {Array} Sorted players array
 */
export function sortPlayersByScore(players) {
  return [...players].sort(
    (a, b) => (b.average_score || 0) - (a.average_score || 0)
  );
}

/**
 * Ranked distribution - Top players go to first teams
 * Top N players go to Team 1, next N to Team 2, etc.
 *
 * @param {Array} players - Sorted array of players (highest score first)
 * @param {number} numTeams - Number of teams to create
 * @returns {Array} Array of team arrays
 */
export function distributeRanked(players, numTeams) {
  const teams = Array.from({ length: numTeams }, (_, i) => ({
    name: `Team ${i + 1}`,
    players: [],
  }));

  const playersPerTeam = Math.ceil(players.length / numTeams);

  players.forEach((player, index) => {
    // Assign top players to first teams, clamp to last team for remainder
    const teamIndex = Math.min(
      Math.floor(index / playersPerTeam),
      numTeams - 1
    );
    teams[teamIndex].players.push(player);
  });

  return teams;
}

/**
 * Balanced distribution using snake draft
 * Alternating direction: 1,2,3,4,4,3,2,1,1,2,3,4...
 * This balances team average scores
 *
 * @param {Array} players - Sorted array of players (highest score first)
 * @param {number} numTeams - Number of teams to create
 * @returns {Array} Array of team arrays
 */
export function distributeBalanced(players, numTeams) {
  const teams = Array.from({ length: numTeams }, (_, i) => ({
    name: `Team ${i + 1}`,
    players: [],
  }));

  let direction = 1;
  let teamIndex = 0;

  players.forEach((player) => {
    teams[teamIndex].players.push(player);

    // Move to next team
    teamIndex += direction;

    // Reverse direction at boundaries
    if (teamIndex >= numTeams) {
      direction = -1;
      teamIndex = numTeams - 1;
    } else if (teamIndex < 0) {
      direction = 1;
      teamIndex = 0;
    }
  });

  return teams;
}

/**
 * Hybrid distribution with different modes for team ranges
 *
 * @param {Array} players - Sorted array of players (highest score first)
 * @param {Array} teamGroupings - Array of { startTeam, endTeam, mode } objects
 * @param {number} totalTeams - Total number of teams
 * @returns {Array} Array of team arrays
 *
 * @example
 * // Teams 1-2 ranked (best players), Teams 3-4 balanced
 * distributeHybrid(players, [
 *   { startTeam: 1, endTeam: 2, mode: 'ranked' },
 *   { startTeam: 3, endTeam: 4, mode: 'balanced' }
 * ], 4)
 */
export function distributeHybrid(players, teamGroupings, totalTeams) {
  // Initialize all teams
  const teams = Array.from({ length: totalTeams }, (_, i) => ({
    name: `Team ${i + 1}`,
    players: [],
  }));

  // Sort groupings by startTeam to process in order
  const sortedGroupings = [...teamGroupings].sort(
    (a, b) => a.startTeam - b.startTeam
  );

  // Calculate total players per grouping based on proportional team count
  let remainingPlayers = [...players];

  sortedGroupings.forEach((grouping, groupIndex) => {
    const numTeamsInGroup = grouping.endTeam - grouping.startTeam + 1;

    // Calculate how many players for this grouping
    // For last grouping, use all remaining players
    let playersForGroup;
    if (groupIndex === sortedGroupings.length - 1) {
      playersForGroup = remainingPlayers;
    } else {
      // Proportional distribution based on team count
      const remainingTeams = sortedGroupings
        .slice(groupIndex)
        .reduce((sum, g) => sum + (g.endTeam - g.startTeam + 1), 0);

      const playersPerTeam = Math.ceil(remainingPlayers.length / remainingTeams);
      const count = playersPerTeam * numTeamsInGroup;
      playersForGroup = remainingPlayers.splice(0, count);
    }

    // Distribute within this grouping
    const groupTeams =
      grouping.mode === "ranked"
        ? distributeRankedToTeams(playersForGroup, numTeamsInGroup)
        : distributeBalancedToTeams(playersForGroup, numTeamsInGroup);

    // Assign to correct team indices
    groupTeams.forEach((teamPlayers, i) => {
      const teamIndex = grouping.startTeam - 1 + i;
      if (teamIndex < teams.length) {
        teams[teamIndex].players = teamPlayers;
      }
    });
  });

  return teams;
}

// Helper: distribute and return just player arrays (for hybrid)
function distributeRankedToTeams(players, numTeams) {
  const teams = Array.from({ length: numTeams }, () => []);
  const playersPerTeam = Math.ceil(players.length / numTeams);

  players.forEach((player, index) => {
    const teamIndex = Math.min(
      Math.floor(index / playersPerTeam),
      numTeams - 1
    );
    teams[teamIndex].push(player);
  });
  return teams;
}

function distributeBalancedToTeams(players, numTeams) {
  const teams = Array.from({ length: numTeams }, () => []);
  let direction = 1;
  let teamIndex = 0;

  players.forEach((player) => {
    teams[teamIndex].push(player);
    teamIndex += direction;

    if (teamIndex >= numTeams) {
      direction = -1;
      teamIndex = numTeams - 1;
    } else if (teamIndex < 0) {
      direction = 1;
      teamIndex = 0;
    }
  });

  return teams;
}

/**
 * Calculate team statistics
 * @param {Array} team - Array of players in a team
 * @returns {Object} Statistics object
 */
export function calculateTeamStats(team) {
  if (!team.players || team.players.length === 0) {
    return {
      count: 0,
      averageScore: 0,
      minScore: 0,
      maxScore: 0,
    };
  }

  const scores = team.players
    .map((p) => p.average_score || 0)
    .filter((s) => s > 0);

  if (scores.length === 0) {
    return {
      count: team.players.length,
      averageScore: 0,
      minScore: 0,
      maxScore: 0,
    };
  }

  return {
    count: team.players.length,
    averageScore: scores.reduce((sum, s) => sum + s, 0) / scores.length,
    minScore: Math.min(...scores),
    maxScore: Math.max(...scores),
  };
}

/**
 * Move a player from one team to another
 * @param {Array} teams - Array of team objects
 * @param {string} playerId - ID of the player to move
 * @param {number} fromTeamIndex - Index of the source team
 * @param {number} toTeamIndex - Index of the destination team
 * @returns {Array} New teams array with the player moved
 */
export function movePlayer(teams, playerId, fromTeamIndex, toTeamIndex) {
  const newTeams = teams.map((team) => ({
    ...team,
    players: [...team.players],
  }));

  // Find and remove player from source team
  const playerIndex = newTeams[fromTeamIndex].players.findIndex(
    (p) => p.id === playerId
  );

  if (playerIndex === -1) return teams;

  const [player] = newTeams[fromTeamIndex].players.splice(playerIndex, 1);

  // Add to destination team
  newTeams[toTeamIndex].players.push(player);

  return newTeams;
}

/**
 * Generate default team groupings (all teams use same mode)
 * @param {number} numTeams - Number of teams
 * @param {string} mode - Distribution mode ('ranked' or 'balanced')
 * @returns {Array} Array with single grouping
 */
export function createDefaultGrouping(numTeams, mode = "balanced") {
  return [
    {
      startTeam: 1,
      endTeam: numTeams,
      mode,
    },
  ];
}

/**
 * Filter players based on criteria
 * @param {Array} players - Array of players
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered players
 */
export function filterPlayers(players, filters = {}) {
  let filtered = [...players];

  // Filter by groups
  if (filters.groups && filters.groups.length > 0) {
    filtered = filtered.filter(
      (p) => p.group && filters.groups.includes(p.group)
    );
  }

  // Filter by scored status
  if (!filters.includeUnscored) {
    filtered = filtered.filter(
      (p) => p.average_score !== null && p.average_score !== undefined
    );
  }

  return filtered;
}
